/**
 * Patient portal appointments viewer — the logged-in patient's upcoming
 * appointments, split into ZUB (zubní ordinace) and DH (dentální hygiena).
 * URL patient id is cosmetic: data always comes from the session; a mismatch
 * redirects to the patient's own page.
 */

import type { AppRouter } from '@dentaly/server/trpc'
import { formatPrice } from '@dentaly/shared'
import { useQueryClient } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import { Clock, LogOut } from 'lucide-react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { formatDateCzech } from '@/lib/calendar-utils'
import {
	PortalCentered,
	PortalLoading,
	PortalShell,
	portalButtonOutline,
	portalHeading,
	portalHeadingBar,
	portalLabel,
	portalPanel,
} from '@/routes/appointments/portal-shell'
import { trpc } from '@/trpc/client'

// Inferred from the router so the shape can never drift from the server
type PortalAppointment =
	inferRouterOutputs<AppRouter>['patientPortal']['myUpcomingAppointments'][number]

function formatPriceRange(
	priceFrom: number | null,
	priceTo: number | null,
): string | null {
	if (priceFrom === null && priceTo === null) return null
	if (priceFrom !== null && priceTo !== null && priceFrom !== priceTo) {
		return `${formatPrice(priceFrom, 'CZK')} – ${formatPrice(priceTo, 'CZK')}`
	}
	return formatPrice((priceFrom ?? priceTo) as number, 'CZK')
}

function AppointmentItem({ appointment }: { appointment: PortalAppointment }) {
	const price = formatPriceRange(appointment.priceFrom, appointment.priceTo)
	return (
		<div className="py-5 first:pt-0 last:pb-0">
			<p className={`${portalHeading} text-base capitalize leading-snug`}>
				{formatDateCzech(appointment.date, 'EEEE d. MMMM yyyy')}
			</p>
			<p className="mt-1 flex items-center gap-2 text-base">
				<Clock className="h-4 w-4 text-[#d7ad6c]" />
				{appointment.startTime}–{appointment.endTime}
			</p>
			{appointment.note && <p className="mt-2 text-base">{appointment.note}</p>}
			{price && <p className="mt-1 text-sm text-[#919194]">Cena: {price}</p>}
		</div>
	)
}

function AppointmentSection({
	title,
	appointments,
}: {
	title: string
	appointments: PortalAppointment[]
}) {
	return (
		<section className={`${portalPanel} p-6 sm:p-8`}>
			<h2
				className={`${portalHeading} ${portalHeadingBar} mb-6 text-[22px] leading-tight`}
			>
				{title}
			</h2>
			{appointments.length === 0 ? (
				<p className="text-base">Žádné nadcházející objednávky</p>
			) : (
				<div className="divide-y divide-[#dedede]">
					{appointments.map((appointment) => (
						<AppointmentItem key={appointment.id} appointment={appointment} />
					))}
				</div>
			)}
		</section>
	)
}

export function PatientAppointmentsPage() {
	const { patientId } = useParams<{ patientId: string }>()
	const navigate = useNavigate()
	const queryClient = useQueryClient()

	const { data: me } = trpc.patientPortal.me.useQuery(undefined, {
		retry: false,
	})
	const {
		data: appointments,
		isLoading,
		isError,
		refetch,
	} = trpc.patientPortal.myUpcomingAppointments.useQuery()

	const logout = trpc.patientPortal.logout.useMutation({
		onSuccess: () => {
			queryClient.clear()
			navigate('/appointments/login', { replace: true })
		},
		// A silent logout failure on a shared computer leaves a live 7-day
		// session behind while the user believes they are signed out
		onError: () => {
			toast.error('Odhlášení se nezdařilo. Zkuste to prosím znovu.')
		},
	})

	if (!me || isLoading) {
		return <PortalLoading />
	}

	// A failed query must never render as "no appointments" — a patient told
	// the schedule is empty during an outage may miss a real visit
	if (isError) {
		return (
			<PortalCentered>
				<div
					className={`${portalPanel} w-full max-w-md space-y-4 p-8 text-center`}
				>
					<p className="text-[#242424]">
						Objednávky se nepodařilo načíst. Zkontrolujte připojení a zkuste to
						prosím znovu.
					</p>
					<Button className={portalButtonOutline} onClick={() => refetch()}>
						Zkusit znovu
					</Button>
				</div>
			</PortalCentered>
		)
	}

	// Session decides whose data is shown — a foreign id in the URL just redirects
	if (patientId !== me.id) {
		return <Navigate to={`/appointments/${me.id}`} replace />
	}

	const zub = (appointments ?? []).filter((a) => a.type === 'ZUB')
	const dh = (appointments ?? []).filter((a) => a.type === 'DH')

	return (
		<PortalShell>
			<header className="border-b border-[#dedede] bg-white">
				<div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4 sm:px-6">
					{/* Text rendition of the site logo */}
					<p className={`${portalHeading} text-2xl leading-none`}>Josenika</p>
					<Button
						className={portalButtonOutline}
						size="sm"
						onClick={() => logout.mutate()}
						disabled={logout.isPending}
					>
						<LogOut className="mr-2 h-4 w-4" />
						Odhlásit se
					</Button>
				</div>
			</header>

			<main className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-14">
				<div className="mb-10 text-center">
					<span className={portalLabel}>Pacientský portál</span>
					<h1
						className={`${portalHeading} mt-2 text-[28px] leading-tight sm:text-[34px]`}
					>
						Moje objednávky
					</h1>
					<p className="mt-2 text-base">
						{me.firstName} {me.lastName}
					</p>
				</div>

				<div className="space-y-8">
					<AppointmentSection title="Zubní ordinace" appointments={zub} />
					<AppointmentSection title="Dentální hygiena" appointments={dh} />
				</div>
			</main>
		</PortalShell>
	)
}
